// Archives (v1.39, app.md §9). A read-only lookup of terminal invoices — the two
// Point Click Care completions and the three archived states. No work actions:
// each card carries only Open, and the detail modal is view-only. Newest-first.
//
// Two things set it apart from the queue screens:
//  - It never loads the whole set (terminal rows grow without bound). It pages
//    one screenful at a time, with a Load More button.
//  - The shared header filter is applied server-side, so filtering and paging
//    compose — the queue screens filter their loaded page in memory, which would
//    be wrong across pages here.
//
// PORT NOTE: the filter used to be compiled into a query string here and the page
// cursor was the store's own paging token. Both are the API's business now: named
// filter parameters go in, an opaque cursor comes back, and the client never looks
// inside it.
//
// Row visibility is the API's, not app logic: a company-scoped viewer sees only
// their company's completed invoices; internal roles see all. The PDF pane shows
// only where the profile allows document access (canPreviewPdf), which since
// 2026-08-10 includes a company approver at any of the three tiers (owner ruling
// R2; docs/security.md §5 owns the policy and the exposure it accepts).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Caption1, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowDownRegular } from '@fluentui/react-icons'
import { ScreenIntro } from '../components/ScreenIntro'
import type { Invoice } from '../lib/types'
import { canPreviewPdf, type Session } from '../lib/identity'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { loadArchivePage } from '../lib/data'
import { errorMessage } from '../api/errors'
import { isFilterActive, type InvoiceFilter } from '../lib/filters'
import { TERMINAL_STATUSES } from '@ap/shared'
import { INITIAL_REFRESH, queueRefreshAction, type RefreshSignal } from '../lib/refresh'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

// One screenful per fetch — enough to fill the viewport, small enough that an
// accidental Archives selection never pulls thousands of rows.
const PAGE_SIZE = 50

const useStyles = makeStyles({
  more: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '20px 0 4px' },
  count: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  session: Session
  /** The shell's refresh signal (D-160). The Refresh button reloads page 1; a
      silent refresh (Pipeline button, 30-second timer) does too, but only while
      page 1 alone is loaded and no invoice is open. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button), applied server-side here. */
  filter: InvoiceFilter
}

export function ArchivesScreen({ session, refresh = INITIAL_REFRESH, filter }: Props) {
  const s = useStyles()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)

  const showPdf = useMemo(() => canPreviewPdf(session), [session])
  // Stable key for the filter, so page 1 reloads when the filter changes without
  // re-running on every render from a new object identity.
  const filterKey = JSON.stringify(filter)

  // Bumped on every page-1 load. A loadMore that resolves after the filter
  // changed is discarded, so a stale page can't append old-filter rows or
  // overwrite the new query's cursor.
  const queryVersion = useRef(0)
  // How many pages are on screen. A silent refresh replaces the list with page
  // 1, so it runs only while this is 1 — it must not throw away pages the reader
  // asked for with Load More.
  const pagesLoaded = useRef(0)

  // Page 1: on mount, whenever the filter changes, and on a refresh. `silent`
  // (a refresh nobody asked for) keeps the rows on screen, shows no spinner and
  // swallows a failure.
  const loadFirst = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent
    const version = ++queryVersion.current
    if (!silent) {
      setLoading(true)
      setError(null)
    }
    try {
      const page = await loadArchivePage(TERMINAL_STATUSES, filter, undefined, PAGE_SIZE)
      if (queryVersion.current !== version) return // superseded by a newer load
      setInvoices(page.rows)
      setCursor(page.cursor)
      pagesLoaded.current = 1
      if (silent) setError(null)
    } catch (e) {
      if (queryVersion.current === version && !silent) setError(errorMessage(e))
    } finally {
      if (queryVersion.current === version) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on filterKey (stable serialization), not the filter object identity
  }, [filterKey])

  // Mount and filter change: never silent, there is nothing to keep.
  useEffect(() => {
    loadFirst()
  }, [loadFirst])

  // THE SHELL'S REFRESH SIGNAL (D-160), with the same pause rule as `useQueue`:
  // nothing moves under the open invoice or a Load More in flight, and the
  // catch-up runs silently afterwards. A silent refresh is skipped once a second
  // page is on screen; the Refresh button still reloads page 1.
  const paused = openInvoice !== null || loadingMore
  const seenSeq = useRef<number | null>(null)
  const stale = useRef(false)
  useEffect(() => {
    if (seenSeq.current === null) {
      seenSeq.current = refresh.seq
      return
    }
    const seqChanged = refresh.seq !== seenSeq.current
    seenSeq.current = refresh.seq
    const action = queueRefreshAction({
      seqChanged,
      silent: refresh.silent,
      paused,
      stale: stale.current,
      extraPagesLoaded: pagesLoaded.current > 1,
    })
    if (action === 'mark-stale') stale.current = true
    else if (action === 'reload') {
      stale.current = false
      void loadFirst()
    } else if (action === 'reload-silent') {
      stale.current = false
      void loadFirst({ silent: true })
    } else if (!paused) stale.current = false
  }, [refresh.seq, refresh.silent, paused, loadFirst])

  async function loadMore() {
    if (!cursor) return
    const version = queryVersion.current
    setLoadingMore(true)
    setError(null)
    try {
      const page = await loadArchivePage(TERMINAL_STATUSES, filter, cursor, PAGE_SIZE)
      if (queryVersion.current !== version) return // filter changed mid-flight — discard
      setInvoices((prev) => [...prev, ...page.rows])
      setCursor(page.cursor)
      pagesLoaded.current += 1
    } catch (e) {
      if (queryVersion.current === version) setError(errorMessage(e))
    } finally {
      if (queryVersion.current === version) setLoadingMore(false)
    }
  }

  const filtered = isFilterActive(filter)

  return (
    <div>
      <ScreenIntro>View completed invoices.</ScreenIntro>

      <ErrorBanner message={error} />

      {loading ? (
        <Loading label="Loading archived invoices…" />
      ) : invoices.length === 0 ? (
        <EmptyQueue text={filtered ? 'No invoices match the current filters.' : 'Nothing in the archive yet.'} />
      ) : (
        <>
          <InvoiceQueue
            invoices={invoices}
            screen="archives"
            columns="amounts"
            onOpen={setOpenInvoice}
            codingDisplay="halves"
          />
          <div className={s.more}>
            <Caption1 className={s.count}>
              Showing {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
              {cursor ? ' · more available' : ''}
            </Caption1>
            {cursor && (
              <Button icon={<ArrowDownRegular />} onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            )}
          </div>
        </>
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf={showPdf}
        user={session.user}
        screen="archives"
        onClose={() => setOpenInvoice(null)}
      />
    </div>
  )
}
